import { RootState } from '@/store/index';
import { ActionContext, ActionTree } from 'vuex';
import { ApplicationUserState } from '@/store/modules/application-user/index';
import {
  APPLICATION_USER_RESET_PASSWORD,
  GET_APPLICATION_USER_INFO,
  GET_USER_CONTACT_DETAILS,
  SET_APPLICATION_USER_INFO_ERRORS,
  SET_APPLICATION_USER_LOADING,
  SET_CHANGE_APP_USER_DETAILS_MODAL_ERRORS,
  SET_CURRENT_APP_USER_DETAILS,
  SET_OPEN_CHANGE_APP_USER_DETAILS_MODAL,
  SET_USER_ADDITIONAL_INFO_LIST,
  SET_USER_INFO_LIST,
  UNLOCK_USER,
  UPDATE_USER_CONTACT_DETAILS,
} from '@/types/application-users.types';
import ApplicationUserService from '@/services/application-user.service';
import { finalize } from 'rxjs/operators';
import { Grid } from '@/models/Grid.model';
import {
  BaseUserContactDetailsModel,
  UserContactDetailsModel,
} from '@/models/application-user/user-contact-details.model';
import { eventsHub } from '@/main';

export const applicationUserService = new ApplicationUserService();

export const actions: ActionTree<ApplicationUserState, RootState> = {

  [GET_APPLICATION_USER_INFO]: (
    { commit, dispatch, state }: ActionContext<ApplicationUserState, RootState>,
    idNumber: number,
  ) => {
    commit(SET_APPLICATION_USER_LOADING, true);
    applicationUserService.getApplicationUserInfo(state.applicationUserSearchObject.idNumber)
      .pipe(
        finalize(() => commit(SET_APPLICATION_USER_LOADING, false)),
      )
      .subscribe(
        (data) => {
          commit(SET_USER_INFO_LIST, {
            results: data.userInfoList,
            resultCount: data.userInfoList.length,
          });
          commit(SET_USER_ADDITIONAL_INFO_LIST, {
            results: data.userAdditionalInfoList,
            resultCount: data.userAdditionalInfoList.length,
          });
        },
        error => commit(SET_APPLICATION_USER_INFO_ERRORS, error.messages),
      );
  },

  [GET_USER_CONTACT_DETAILS]: (
    { commit, dispatch, state }: ActionContext<ApplicationUserState, RootState>,
    userDetails: BaseUserContactDetailsModel,
  ) => {
    commit(SET_APPLICATION_USER_LOADING, true);
    applicationUserService.getUserContactDetails(userDetails)
      .pipe(
        finalize(() => commit(SET_APPLICATION_USER_LOADING, false)),
      )
      .subscribe(
        (data) => {
          commit(SET_CURRENT_APP_USER_DETAILS, { ...data, ...userDetails });
          commit(SET_OPEN_CHANGE_APP_USER_DETAILS_MODAL, true);
        },
        () => {
          eventsHub.$notify({
            group: 'frd',
            type: 'error',
            text: '' + eventsHub.$t('application_user.get_application_user_contact_details_error_message'),
          });
        },
      );
  },

  [UPDATE_USER_CONTACT_DETAILS]: (
    { commit, dispatch, state }: ActionContext<ApplicationUserState, RootState>,
    userDetails: UserContactDetailsModel,
  ) => {
    commit(SET_APPLICATION_USER_LOADING, true);
    applicationUserService.updateUserContactDetails(userDetails)
      .pipe(
        finalize(() => commit(SET_APPLICATION_USER_LOADING, false)),
      )
      .subscribe(
        () => {
          eventsHub.$notify({
            group: 'frd',
            type: 'success',
            text: '' + eventsHub.$t('application_user.update_user_contact_details_success_message'),
          });
          commit(SET_OPEN_CHANGE_APP_USER_DETAILS_MODAL, false);
        },
        error => commit(SET_CHANGE_APP_USER_DETAILS_MODAL_ERRORS, error.messages),
      );
  },

  [UNLOCK_USER]: (
    { commit, dispatch, state }: ActionContext<ApplicationUserState, RootState>,
    userDetails: BaseUserContactDetailsModel,
  ) => {
    commit(SET_APPLICATION_USER_LOADING, true);
    applicationUserService.unLockUser(userDetails)
      .pipe(
        finalize(() => commit(SET_APPLICATION_USER_LOADING, false)),
      )
      .subscribe(
        (data) => {
          eventsHub.$notify({
            group: 'frd',
            type: 'success',
            text: '' + eventsHub.$t('application_user.unlock_user_success_message'),
          });
        },
        () => {
          eventsHub.$notify({
            group: 'frd',
            type: 'error',
            text: '' + eventsHub.$t('application_user.unlock_user_error_message'),
          });
        },
      );
  },

  [APPLICATION_USER_RESET_PASSWORD]: (
    { commit, dispatch, state }: ActionContext<ApplicationUserState, RootState>,
    userDetails: BaseUserContactDetailsModel,
  ) => {
    commit(SET_APPLICATION_USER_LOADING, true);
    applicationUserService.resetPassword(userDetails)
      .pipe(
        finalize(() => commit(SET_APPLICATION_USER_LOADING, false)),
      )
      .subscribe(
        (data) => {
          eventsHub.$notify({
            group: 'frd',
            type: 'success',
            text: '' + eventsHub.$t('application_user.reset_password_success_message'),
          });
        },
        error => {
          eventsHub.$notify({
            group: 'frd',
            type: 'error',
            text: '' + eventsHub.$t('application_user.reset_password_error_message'),
          });
        },
      );
  },
};
